import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

type Props = {
  enabled?: boolean;
  thresholdPx?: number;
};

export const PullToRefreshReload: React.FC<Props> = ({ enabled = true, thresholdPx = 80 }) => {
  const startYRef = React.useRef<number | null>(null);
  const pullingRef = React.useRef(false);

  const [pullDistance, setPullDistance] = React.useState(0);
  const [armed, setArmed] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);

  const canStart = () => {
    return enabled && !refreshing && window.scrollY <= 0;
  };

  const onTouchStart = (e: TouchEvent) => {
    if (!canStart()) return;
    startYRef.current = e.touches[0].clientY;
    pullingRef.current = true;
    setPullDistance(0);
    setArmed(false);
  };

  const onTouchMove = (e: TouchEvent) => {
    if (!pullingRef.current) return;
    if (startYRef.current == null) return;

    const y = e.touches[0].clientY;
    const dy = y - startYRef.current;

    if (dy <= 0) {
      setPullDistance(0);
      setArmed(false);
      return;
    }

    const eased = Math.min(140, dy * 0.6);

    setPullDistance(eased);
    setArmed(eased >= thresholdPx);

    if (window.scrollY <= 0) e.preventDefault();
  };

  const onTouchEnd = () => {
    if (!pullingRef.current) return;

    pullingRef.current = false;
    startYRef.current = null;

    if (armed && !refreshing) {
      setRefreshing(true);
      setTimeout(() => {
        window.location.reload();
      }, 150);
      return;
    }

    setPullDistance(0);
    setArmed(false);
  };

  React.useEffect(() => {
    if (!enabled) return;

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', onTouchStart as any);
      window.removeEventListener('touchmove', onTouchMove as any);
      window.removeEventListener('touchend', onTouchEnd as any);
    };
  }, [enabled, armed, refreshing]);

  const height = pullDistance;

  return (
    <>
      <style>{`
        html, body { overscroll-behavior-y: contain; }
      `}</style>

      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height,
          zIndex: 1300,
          display: height > 0 ? 'flex' : 'none',
          alignItems: 'flex-end',
          justifyContent: 'center',
          pb: 1,
          pointerEvents: 'none',
          transition: refreshing ? 'none' : 'height 80ms linear',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 999,
            bgcolor: 'background.paper',
            boxShadow: 1,
          }}
        >
          {refreshing ? (
            <>
              <CircularProgress size={18} />
              <Typography variant="body2">Обновляю…</Typography>
            </>
          ) : (
            <>
              <CircularProgress
                size={18}
                variant="determinate"
                value={Math.min(100, (pullDistance / thresholdPx) * 100)}
              />
              <Typography variant="body2">
                {armed ? 'Отпусти — обновлю' : 'Потяни вниз — обновлю'}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};
